import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import _ from "lodash";
import { Controller, useFieldArray } from "react-hook-form";
import { ParsingType } from "../../../interfaces/doc";
import ExtractionIcon from "./ConfigExtractionIcons";
import { useFormConfigContext } from "./ConfigFormContext";
import { ExtractionQueryPath } from "./ConfigUtils";
import EnumField from "./EnumField";

const CustomExtractionForm = ({
  isReadOnly,
  queryPath,
}: {
  isReadOnly: boolean;
  queryPath: ExtractionQueryPath;
}) => {
  const { control, watch } = useFormConfigContext();
  const {
    fields: customFields,
    append: addField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: `${queryPath}.custom_schema`,
  });

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" className="font-semibold">
        Champs personnalisés
      </Typography>

      {customFields.map((field, fieldIndex) => {
        const field_parsing_type = watch(
          `${queryPath}.custom_schema.${fieldIndex}.parsing_type`
        );

        return (
          <Paper key={field.id} variant="outlined" className="p-2">
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={10}>
                <Controller
                  control={control}
                  name={`${queryPath}.custom_schema.${fieldIndex}.name`}
                  render={({ field: queryField }) => (
                    <TextField
                      {...queryField}
                      fullWidth
                      size="small"
                      multiline
                      label="Nom du champ"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Controller
                              control={control}
                              name={`${queryPath}.custom_schema.${fieldIndex}.parsing_type`}
                              render={({ field: selectField }) => (
                                <Select
                                  size="small"
                                  variant="standard"
                                  disableUnderline
                                  sx={{
                                    minWidth: 120,
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    borderRight: "1px solid #e0e0e0",
                                    marginRight: 1,
                                  }}
                                  {...selectField}
                                >
                                  {_.values([
                                    ParsingType.String,
                                    ParsingType.Int,
                                    ParsingType.Boolean,
                                    ParsingType.Float,
                                    ParsingType.Enum,
                                  ]).map((parsingType) => (
                                    <MenuItem
                                      key={parsingType}
                                      value={parsingType}
                                    >
                                      <div className="flex items-center gap-1">
                                        <ExtractionIcon
                                          parsingType={parsingType}
                                          props={{
                                            fontSize: "small",
                                          }}
                                        />
                                        {parsingType.charAt(0).toUpperCase() +
                                          parsingType.slice(1)}
                                      </div>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
              {!isReadOnly && (
                <Grid2 size={2}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      color="info"
                      onClick={() =>
                        addField({
                          name: "",
                          parsing_type: ParsingType.String,
                        })
                      }
                      size="small"
                      sx={{
                        display:
                          fieldIndex === customFields.length - 1
                            ? "visible"
                            : "none",
                      }}
                    >
                      <Add fontSize="small" />
                    </Button>

                    <Button
                      color="error"
                      onClick={() => removeField(fieldIndex)}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </Button>
                  </Stack>
                </Grid2>
              )}
              {field_parsing_type === ParsingType.Enum && (
                <Grid2 size={12} className="mt-2">
                  <EnumField
                    isReadOnly={isReadOnly}
                    queryPath={`${queryPath}.custom_schema.${fieldIndex}.enum_values`}
                  />
                </Grid2>
              )}
            </Grid2>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default CustomExtractionForm;
